import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  EVALUATION_AXES,
  EVALUATION_TREND_DOMAIN,
  RESOLUTION_AXES,
  RESOLUTION_TREND_DOMAIN,
  type AxisDef,
  type EvalSeriesPoint,
} from './designEvaluation.js';

export interface DesignEvaluationTrendProps {
  /** プロットするデータ (period 昇順) */
  points: EvalSeriesPoint[];
  /** x 軸に並べる period 一覧 (欠損 period は null で枠だけ残す) */
  periods: string[];
  /** period → x 軸の短ラベル */
  shortLabelOf: (period: string) => string;
  /** period → tooltip のフルラベル */
  fullLabelOf: (period: string) => string;
  /** 各折線図の高さ (px) */
  height?: number;
}

interface TrendRow {
  label: string;
  full: string;
  [axisKey: string]: number | string | null;
}

interface TrendTooltipEntry {
  name?: string;
  value?: number | null;
  color?: string;
  payload?: TrendRow;
}

/** @graph-connects none */
function TrendTooltip(props: { active?: boolean; payload?: TrendTooltipEntry[] }) {
  if (!props.active || !props.payload || props.payload.length === 0) return null;
  const full = props.payload[0]?.payload?.full;
  return (
    <div className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg text-xs">
      <div className="text-[var(--color-text-muted)] mb-1">{full}</div>
      {props.payload.map((e) => (
        <div key={e.name} className="tabular-nums" style={{ color: e.color }}>
          {e.name}: {e.value == null ? '—' : Number(e.value).toFixed(2)}
        </div>
      ))}
    </div>
  );
}

/** 1 つの推移折線 (解像度 or 評価)。 @graph-connects none */
function EvalTrend<K extends string>({
  axes,
  field,
  points,
  periods,
  shortLabelOf,
  fullLabelOf,
  height,
  domain,
}: {
  axes: readonly AxisDef<K>[];
  field: 'resolution' | 'evaluation';
  points: EvalSeriesPoint[];
  periods: string[];
  shortLabelOf: (period: string) => string;
  fullLabelOf: (period: string) => string;
  height: number;
  /** y 軸スケール ([0, 満点])。解像度=3 / 貢献度=5 */
  domain: [number, number];
}) {
  const { t } = useTranslation();

  const rows = useMemo<TrendRow[]>(() => {
    const byPeriod = new Map(points.map((p) => [p.period, p]));
    return periods.map((per) => {
      const p = byPeriod.get(per);
      const row: TrendRow = { label: shortLabelOf(per), full: fullLabelOf(per) };
      for (const a of axes) {
        row[a.key] = p ? ((p[field] as Record<K, number | null>)[a.key] ?? null) : null;
      }
      return row;
    });
  }, [axes, field, points, periods, shortLabelOf, fullLabelOf]);

  return (
    <div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: 'var(--color-border)' }}
              interval={0}
              minTickGap={0}
            />
            <YAxis
              domain={domain}
              tickCount={domain[1] - domain[0] + 1}
              tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={32}
            />
            <RTooltip
              cursor={{ stroke: 'var(--color-border-hover)', strokeWidth: 1 }}
              content={<TrendTooltip />}
            />
            {axes.map((a) => (
              <Line
                key={a.key}
                type="monotone"
                dataKey={a.key}
                name={t(a.labelKey)}
                stroke={a.color}
                strokeWidth={2}
                dot={{ r: 2.5, fill: 'var(--color-bg-surface)', stroke: a.color, strokeWidth: 1.5 }}
                connectNulls
                isAnimationActive
                animationDuration={800}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* 凡例は軸の宣言順 (解像度は色の浅→深) で固定する */}
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-[10px] text-[var(--color-text-muted)]">
        {axes.map((a) => (
          <span key={a.key} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-0.5 rounded"
              style={{ background: a.color }}
              aria-hidden="true"
            />
            {t(a.labelKey)}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * 全体の評価推移を折線で表示する。PJ解像度 5 軸 (浅→深の teal) と チーム貢献度評価 3 軸
 * (軸別の色) を別々の折線図にし、x 軸 = 時間 (月: FY 12 ヶ月 / Q: 全 Q) / y 軸 = 点数。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン評価カードの推移折線 (PJ解像度 5 軸 + 貢献度 3 軸, 月/Q)
 * @graph-connects none
 */
export function DesignEvaluationTrend({
  points,
  periods,
  shortLabelOf,
  fullLabelOf,
  height = 260,
}: DesignEvaluationTrendProps) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] text-center mb-1">
          {t('idtKpi.designEvaluation.resolutionTitle')}
        </h4>
        <EvalTrend
          axes={RESOLUTION_AXES}
          field="resolution"
          points={points}
          periods={periods}
          shortLabelOf={shortLabelOf}
          fullLabelOf={fullLabelOf}
          height={height}
          domain={RESOLUTION_TREND_DOMAIN}
        />
      </div>
      <div>
        <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] text-center mb-1">
          {t('idtKpi.designEvaluation.evaluationTitle')}
        </h4>
        <EvalTrend
          axes={EVALUATION_AXES}
          field="evaluation"
          points={points}
          periods={periods}
          shortLabelOf={shortLabelOf}
          fullLabelOf={fullLabelOf}
          height={height}
          domain={EVALUATION_TREND_DOMAIN}
        />
      </div>
    </div>
  );
}
